"use client"

import { useState, type FormEvent } from "react"
import { useRouter } from "next/navigation"
import { Mail, Lock, Eye, EyeOff } from "lucide-react"
import { useLoginMutation } from "@/lib/services/api"

export default function LoginPage() {
  const [loading, setLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({})
  const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null)
  const router = useRouter()
  const [login] = useLoginMutation()

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    return emailRegex.test(email)
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setErrors({})
    setMessage(null)

    // Validation
    const newErrors: { email?: string; password?: string } = {}
    if (!email) {
      newErrors.email = "Please input your email!"
    } else if (!validateEmail(email)) {
      newErrors.email = "Please enter a valid email!"
    }
    if (!password) {
      newErrors.password = "Please input your password!"
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      const result = await login({ email, password }).unwrap()
      // Token persistence is handled in the API service; also set a simple user flag for route guards
      localStorage.setItem("dompetAmanUser", JSON.stringify({ email, role: "admin" }))
      setMessage({ type: "success", text: "Login successful!" })
      setTimeout(() => router.push("/search"), 300)
    } catch (err: any) {
      const errorText =
        err?.data?.message ||
        err?.error ||
        "Login failed. Please check your credentials and try again."
      setMessage({ type: "error", text: errorText })
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex">
      {/* Left side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-background">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <div className="text-center">
            <div className="inline-flex items-center gap-3 mb-2">
              <div className="p-2 bg-[#262626] rounded-lg">
                <img src="/assets/logo_white.svg" alt="DA Logo" className="w-full h-full" />
              </div>
              {/*<span className="text-2xl font-bold text-foreground">dompet aman</span>*/}
            </div>
          </div>

          {/* Welcome Text */}
          <div className="text-center space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Welcome to</h1>
            <h2 className="text-3xl font-bold text-foreground">Dompet Aman</h2>
            <p className="text-muted-foreground">Admin Panel - Sign in to continue</p>
          </div>

          {/* Message */}
          {message && (
            <div
              className={`p-4 rounded-lg ${
                message.type === "success"
                  ? "bg-green-50 text-green-800 border border-green-200"
                  : "bg-red-50 text-red-800 border border-red-200"
              }`}
            >
              {message.text}
            </div>
          )}

          {/* Login Form */}
          <form onSubmit={onSubmit} className="space-y-6">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-foreground">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className={`w-full h-12 pl-10 pr-4 rounded-lg border ${
                    errors.email ? "border-red-500" : "border-input"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-[#E89B5A] focus:border-transparent`}
                />
              </div>
              {errors.email && <p className="text-sm text-red-500">{errors.email}</p>}
            </div>

            <div className="space-y-2">
              <label htmlFor="password" className="block text-sm font-medium text-foreground">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className={`w-full h-12 pl-10 pr-12 rounded-lg border ${
                    errors.password ? "border-red-500" : "border-input"
                  } bg-background focus:outline-none focus:ring-2 focus:ring-[#E89B5A] focus:border-transparent`}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && <p className="text-sm text-red-500">{errors.password}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#E89B5A] hover:bg-[#d88a4a] text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          {/* Test Credentials Info */}
          {/* <div className="text-center text-sm text-muted-foreground bg-muted p-4 rounded-lg">
            <p className="font-semibold mb-1">Test Login Credentials:</p>
            <p>Email: admin@dompetaman.com</p>
            <p>Password: admin123</p>
          </div> */}
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-400 to-orange-500">
          <img src="/assets/login-bg.png" alt="Dompet Aman - Financial Growth" className="w-full h-full object-cover" />
        </div>
      </div>
    </div>
  )
}
